import { Fragment, Suspense, useRef, useState } from 'react'

import { Dialog, Transition } from '@headlessui/react'

import { isSupportEmail } from '@bettermode/common/feature-flags'
import { AdminPanelProvider } from '@tribeplatform/react-components/AdminPanel'
import { useStaticIntl } from '@tribeplatform/react-components/i18n'
import { SquareLogo } from '@tribeplatform/react-components/Logo'
import { useRouter } from '@tribeplatform/react-sdk'
import { useAuthToken } from '@tribeplatform/react-sdk/hooks'
import { SlateKitProvider } from '@tribeplatform/react-slate-kit/components'
import { useSlateKit } from '@tribeplatform/react-slate-kit/hooks'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { getRegisteredBlocks } from '../../../registered-blocks.js'
import { ErrorBoundary } from '../../Error/ErrorBoundry.js'
import { CustomizerProvider } from '../Editor/hooks/index.js'
import { Wrapper } from '../Editor/SlateWrappers/index.js'
import { ScrollProvider } from '../ScrollProvider.js'

import { AdminSidebar } from './AdminSidebar.js'

export const AdminMenu = ({ children }) => {
  const { Link } = useRouter()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { data: authToken } = useAuthToken()
  const containerRef = useRef<HTMLDivElement>()
  const { network, member } = authToken

  const registeredBlocks = useStaticIntl(getRegisteredBlocks)
  const decisionDynamicBlockSettings = isSupportEmail(member?.email ?? '')
  const slateKit = useSlateKit({
    blocks: registeredBlocks,
    blockWrapper: Wrapper,
    dynamicBlockSettings: decisionDynamicBlockSettings,
  })
  const networkExtraProperties = network?.extraProperties ?? []
  const networkTermsOfServiceUrl =
    networkExtraProperties.find(
      property => property.key === 'termsOfServiceUrl',
    )?.value ?? ''
  const networkPrivacyPolicyUrl =
    networkExtraProperties.find(property => property.key === 'privacyPolicyUrl')
      ?.value ?? ''

  return (
    <AdminPanelProvider>
      <SlateKitProvider
        kit={slateKit}
        context={{
          networkTermsOfServiceUrl:
            networkTermsOfServiceUrl ?? network?.termsOfServiceUrl,
          networkExtraProperties:
            networkExtraProperties ?? network?.extraProperties,
          networkPrivacyPolicyUrl:
            networkPrivacyPolicyUrl ?? network?.privacyPolicyUrl,
          actorId: member?.id,
          networkId: network?.id,
        }}
      >
        <CustomizerProvider>
          <div className="flex min-h-screen bg-background text-content-on-background overflow-hidden">
            <div className="hidden lg:block">
              <AdminSidebar />
            </div>
            <div className="flex-grow overflow-hidden">
              <div className="h-screen overflow-auto" ref={containerRef}>
                <div className="contents lg:hidden">
                  <Transition.Root show={sidebarOpen} as={Fragment}>
                    <Dialog
                      as="div"
                      className="fixed inset-0 z-40 flex lg:hidden"
                      onClose={setSidebarOpen}
                    >
                      <Transition.Child
                        as={Fragment}
                        enter="transition-opacity ease-linear duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity ease-linear duration-300"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                      >
                        <Dialog.Overlay className="fixed inset-0 bg-surface-600 bg-opacity-75" />
                      </Transition.Child>
                      <Transition.Child
                        as={Fragment}
                        enter="transition ease-in-out duration-300 transform"
                        enterFrom="-translate-x-full"
                        enterTo="translate-x-0"
                        leave="transition ease-in-out duration-300 transform"
                        leaveFrom="translate-x-0"
                        leaveTo="-translate-x-full"
                      >
                        <div className="relative max-w-md bg-main-50 flex-1 flex flex-col">
                          <Transition.Child
                            as={Fragment}
                            enter="ease-in-out duration-300"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="ease-in-out duration-300"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                          >
                            <div className="absolute top-0 right-0 -mr-14 p-1">
                              <button
                                type="button"
                                className="h-12 w-12 rounded-full flex items-center justify-center focus:outline-none focus:bg-surface-600"
                                onClick={() => setSidebarOpen(false)}
                              >
                                <SvgIcon
                                  size="xl"
                                  className="text-white"
                                  name="close"
                                />
                                <span className="sr-only">Close sidebar</span>
                              </button>
                            </div>
                          </Transition.Child>
                          <div className="flex-shrink-0 flex">
                            <AdminSidebar
                              onSelect={() => setSidebarOpen(false)}
                            />
                          </div>
                        </div>
                      </Transition.Child>
                      <div className="flex-shrink-0 w-14" aria-hidden="true">
                        {/* Dummy element to force sidebar to shrink to fit close icon */}
                      </div>
                    </Dialog>
                  </Transition.Root>
                  <div className="sticky top-0 z-10 px-3 flex-shrink-0 h-16  bg-topbar text-content-on-topbar shadow-topbar border-b border-topbar flex items-center">
                    <button
                      type="button"
                      onClick={() => setSidebarOpen(true)}
                      className="lg:hidden mr-3"
                    >
                      <SvgIcon size="2x" name="menu" />
                    </button>
                    <Link href="/">
                      <SquareLogo />
                    </Link>
                  </div>
                </div>
                <ScrollProvider scrollRef={containerRef}>
                  <ErrorBoundary>
                    <Suspense fallback={<></>}>{children}</Suspense>
                  </ErrorBoundary>
                </ScrollProvider>
              </div>
            </div>
          </div>
        </CustomizerProvider>
      </SlateKitProvider>
    </AdminPanelProvider>
  )
}
